import React, { useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { api } from "@/lib/api";
import { useList, useDeleteOne } from "@/hooks/useApi";
import { useAuth } from "@/context/AuthContext";
import { useQueryClient } from "@tanstack/react-query";
import { PageHeader, Loading, EmptyState, StatusBadge, ProgressBar, fmt } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";

const OPERATIONS = ["Single Needle", "Overlock / Serger", "Flatlock", "Bartack", "Buttonhole", "Button Attach", "Kansai / Waistband", "Feed-off-the-arm", "Blind Hem", "Other"];
const STATUSES = ["Setup", "In Progress", "On Hold", "Completed"];

export default function Stitching() {
  const { canWrite } = useAuth();
  const qc = useQueryClient();
  const delOne = useDeleteOne((id) => `/stitching/${id}`, ["stitching"]);
  const { data: jobs, isLoading } = useList("stitching", "/stitching");
  const { data: orders = [] } = useList("orders", "/orders");
  const { data: machines = [] } = useList("machines", "/machines");
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ order_ref: "", operation_type: "", spi: "", machine_name: "", operator: "", target_quantity: "", stitched_quantity: "", rejection_quantity: "", status: "In Progress", notes: "" });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const submit = async () => {
    if (!f.order_ref) return toast.error("Select order");
    const o = orders.find((x) => x.id === f.order_ref);
    try {
      await api.post("/stitching", { ...f, order_id: o?.order_id, product_name: o?.product_name, spi: Number(f.spi) || 0, target_quantity: Number(f.target_quantity) || 0, stitched_quantity: Number(f.stitched_quantity) || 0, rejection_quantity: Number(f.rejection_quantity) || 0 });
      toast.success("Stitching job created"); qc.invalidateQueries({ queryKey: ["stitching"] }); setOpen(false);
      setF({ order_ref: "", operation_type: "", spi: "", machine_name: "", operator: "", target_quantity: "", stitched_quantity: "", rejection_quantity: "", status: "In Progress", notes: "" });
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  if (isLoading) return <Loading />;
  return (
    <div>
      <PageHeader title="Stitching Department" subtitle="Sewing operations, machines & output tracking" testid="stitching-header">
        {canWrite("production") && <Button onClick={() => setOpen(true)} className="gap-2" data-testid="new-stitching-btn"><Plus className="w-4 h-4" /> New Stitching Job</Button>}
      </PageHeader>
      <Card className="overflow-hidden">
        {!jobs?.length ? <EmptyState title="No stitching jobs" hint="Create a stitching job to start tracking sewing output." /> : (
          <div className="overflow-x-auto"><Table>
            <TableHeader><TableRow>
              <TableHead>Job</TableHead><TableHead>Order</TableHead><TableHead>Operation</TableHead><TableHead>Machine</TableHead>
              <TableHead>Operator</TableHead><TableHead className="w-40">Progress</TableHead><TableHead>Status</TableHead><TableHead></TableHead>
            </TableRow></TableHeader>
            <TableBody>{jobs.map((j) => {
              const pct = j.target_quantity ? Math.min(100, Math.round(j.stitched_quantity / j.target_quantity * 100)) : 0;
              return (
                <TableRow key={j.id} data-testid={`stitching-row-${j.job_id}`}>
                  <TableCell className="font-num font-semibold text-primary">{j.job_id}</TableCell>
                  <TableCell className="font-num">{j.order_id}</TableCell>
                  <TableCell className="text-sm">{j.operation_type}</TableCell>
                  <TableCell className="text-sm">{j.machine_name}</TableCell>
                  <TableCell className="text-sm">{j.operator}</TableCell>
                  <TableCell><div className="flex items-center gap-2"><ProgressBar value={pct} /><span className="text-xs font-num w-9">{pct}%</span></div></TableCell>
                  <TableCell><StatusBadge status={j.status} /></TableCell>
                  <TableCell>{canWrite("production") && <Button size="icon" variant="ghost" onClick={() => delOne(j.id, "Delete this stitching job?")} data-testid={`delete-stitching-${j.job_id}`}><Trash2 className="w-4 h-4 text-destructive" /></Button>}</TableCell>
                </TableRow>
              );
            })}</TableBody>
          </Table></div>
        )}
      </Card>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="stitching-form">
          <DialogHeader><DialogTitle>New Stitching Job</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><Label>Order</Label>
              <Select value={f.order_ref} onValueChange={(v) => set("order_ref", v)}>
                <SelectTrigger data-testid="stitching-order-select"><SelectValue placeholder="Order" /></SelectTrigger>
                <SelectContent>{orders.map((o) => <SelectItem key={o.id} value={o.id}>{o.order_id} — {o.product_name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Operation</Label>
              <Select value={f.operation_type} onValueChange={(v) => set("operation_type", v)}>
                <SelectTrigger data-testid="stitching-operation-select"><SelectValue placeholder="Operation" /></SelectTrigger>
                <SelectContent>{OPERATIONS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>SPI (Stitches/Inch)</Label><Input type="number" value={f.spi} onChange={(e) => set("spi", e.target.value)} /></div>
            <div><Label>Machine</Label>
              <Select value={f.machine_name} onValueChange={(v) => set("machine_name", v)}>
                <SelectTrigger><SelectValue placeholder="Machine" /></SelectTrigger>
                <SelectContent>{machines.map((m) => <SelectItem key={m.id} value={m.name}>{m.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Operator</Label><Input value={f.operator} onChange={(e) => set("operator", e.target.value)} /></div>
            <div><Label>Target Qty</Label><Input type="number" value={f.target_quantity} onChange={(e) => set("target_quantity", e.target.value)} /></div>
            <div><Label>Stitched Qty</Label><Input type="number" value={f.stitched_quantity} onChange={(e) => set("stitched_quantity", e.target.value)} /></div>
            <div><Label>Rejection</Label><Input type="number" value={f.rejection_quantity} onChange={(e) => set("rejection_quantity", e.target.value)} /></div>
            <div><Label>Status</Label>
              <Select value={f.status} onValueChange={(v) => set("status", v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={submit} data-testid="stitching-save-btn">Create</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
